import { ellipseParam } from "lib/geometries/ellipse";
import { lineBaseCreate } from "lib/geometries/line";
import { getCurrentLineMaterial, ILineMaterial } from "lib/materials";
import { eulerAnglesToAxisAngle, normalizeAngle } from "lib/math/angles";
import { ellipseBuffer } from "lib/math/ellipse";
import { mirrorPoint } from "lib/math/mirror";
import { addIpoint, copyIPoint } from "lib/math/point";
import { rotatePoint } from "lib/math/rotate";
import { scalePoint } from "lib/math/scale";
import { IPoint } from "lib/math/types";
import { objDataType } from "../types";
import { LineBaseData } from "./line-base";

export class EllipseData extends LineBaseData {

  public type = objDataType.ELLIPSE;
  protected nameObj: string = "Ellipse";
  public definition: ellipseParam;

  constructor(definition: ellipseParam, material?: ILineMaterial) {
    super();
    this.definition = {
      center: copyIPoint(definition.center),
      a: definition.a,
      b: definition.b,
      azimutO: definition.azimutO,
      plane: copyIPoint(definition.plane)
    };
    this.material = material ?? getCurrentLineMaterial();
  }
  static createObj(definition: ellipseParam, material: ILineMaterial) {
    const buffer = ellipseBuffer(
      definition.center,
      definition.a,
      definition.b,
      definition.azimutO,
      definition.plane
    );
    return lineBaseCreate(buffer, material);
  }
  public createObject(definition?: ellipseParam, material?: ILineMaterial) {
    return EllipseData.createObj(definition ?? this.definition, material ?? this.material);
  }
  public getBufferGeom(): Float32Array {
    return ellipseBuffer(
      this.definition.center,
      this.definition.a,
      this.definition.b,
      this.definition.azimutO,
      this.definition.plane
    );
  }

  public cloneDefinition(): ellipseParam {
    return {
      center: copyIPoint(this.definition.center),
      a: this.definition.a,
      b: this.definition.b,
      azimutO: this.definition.azimutO,
      plane: copyIPoint(this.definition.plane)
    };
  }
  public translate(distance: IPoint) {
    this.definition.center = addIpoint(this.definition.center, distance);
    this.regenerateObjectFromDefinition();
  }
  public rotate(angleX: number, angleY: number, angleZ: number, basePoint: IPoint): void {
    angleX = normalizeAngle(angleX);
    angleY = normalizeAngle(angleY);
    angleZ = normalizeAngle(angleZ);

    this.definition.center = rotatePoint(
      this.definition.center,
      angleX,
      angleY,
      angleZ,
      basePoint
    );
    const { angle } = eulerAnglesToAxisAngle(angleX, angleY, angleZ);
    this.definition.azimutO = normalizeAngle(this.definition.azimutO - angle);
    this.regenerateObjectFromDefinition();
  }
  public scale(factorX: number, factorY: number, factorZ: number, basePoint: IPoint): void {
    this.definition.center = scalePoint(
      this.definition.center,
      factorX,
      factorY,
      factorZ,
      basePoint
    );
    this.definition.a *= factorX;
    this.definition.b *= factorX;
    this.regenerateObjectFromDefinition();
  }
  public mirror(startPoint: IPoint, endPoint: IPoint): void {
    this.definition.center = mirrorPoint(
      this.definition.center,
      startPoint,
      endPoint
    );
    this.regenerateObjectFromDefinition();
  }
  public delete(): void {
    this.graphicObj.geometry.dispose();
  }
}
